import {getCurrentAppName} from '../utils/apputils';
import {getFunArgs, getFunName, isFunction} from './../utils/utils';

type Callback = (...args: any) => void;

/**
 * eventbus 仅限制用于跨模块通讯，组件模块内部场景不要使用
 * @author chentong
 * @date 2023-02-21
 */
export default class EventBus {
  private eventsMap: Map<string, Map<string, Array<Callback>>> = new Map();

  private static _instance: EventBus | undefined;
  private __ONCE__KEY__EVENT__ = '__ONCE__KEY__EVENT__';

  private constructor() {}

  //单例
  static instance(): EventBus {
    if (this._instance === undefined) {
      this._instance = new EventBus();
    }
    return this._instance!!;
  }

  /**
   * 注册事件
   * @param event
   * @param callback
   */
  on(event: string, callback: Callback): void {
    if (!isFunction(callback)) {
      throw new Error(`The second param ${typeof callback} is not a function`);
    }

    const appName = getCurrentAppName();

    const {eventsMap} = this;

    if (!eventsMap.get(appName)) {
      eventsMap.set(appName, new Map());
    }

    const events = eventsMap.get(appName);

    if (!events?.get(event)) {
      events?.set(event, []);
    }

    events?.get(event)?.push(callback);
  }

  /**
   * 解除注册事件
   * @param event
   * @param callback
   * @returns
   */
  off(event: string): void {
    const appName = getCurrentAppName();
    const {eventsMap} = this;
    const events = eventsMap.get(appName);
    if (!events) return;
    if (!events.get(event)) return;
    events.set(event, []);
  }

  /**
   * 发送事件
   * @param event
   * @param args
   */
  emit(event: string, ...args: any): void {
    this.eventsMap.forEach((events, appName) => {
      if (appName == getCurrentAppName()) {
        const callbacks = events?.get(event) || [];
        if (callbacks.length) {
          const argsLength = args?.length || 0;
          for (const callback of callbacks) {
            if (event.startsWith(this.__ONCE__KEY__EVENT__)) {
              callback.call(this, ...args);
              break;
            } else if (getFunArgs(callback).length == argsLength) {
              /** function args参数列表相同才调用 */
              callback.call(this, ...args);
              break;
            }
          }
        }
      }
    });
  }

  emitOnce(event: string, ...args: any): void {
    this.emit(this.__ONCE__KEY__EVENT__ + event, ...args);
  }

  /**
   * 一次性注册,接收事件后自动销毁
   * @param event
   * @param callback
   */
  once(event: string, callback: Callback): void {
    const self = this;
    event = this.__ONCE__KEY__EVENT__ + event;

    function wrap(...args: any) {
      //回调注册方法
      callback.call(self, ...args);
      //解除注册
      self.off(event);
    }

    //注册方法 warp 参数列表不能传递
    self.on(event, wrap);
  }

  /**
   * 按照应用名字清理事件
   * @param appName
   */
  clear(appName: string): void {
    this.eventsMap.set(appName, new Map());
  }
}
